<template>
    <button class="btn btn-primary m-2" @click="openAiScanModal()" v-if="aiScanAllowed">
        <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
    </button>

    <modal id="ai-scan-modal" :title="aiScanModalTitle">
        <div class="scan-results-container">
            <div v-if="isLoading" class="text-center margin-top-1rem d-flex flex-column align-items-center">
                <b-spinner variant="primary" style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
                <p class="mt-4">This may take a few moments. Please wait.</p>
            </div>

            <template v-else>
                <div v-if="currentScan === 0">
                    <div>
                        <h5 class="card-title mb-0">Property Previous Score</h5>
                        <p class="text">{{ wizardData.name }}</p>
                        <div class="mb-4">
                            <AiScoreGraph :score="Math.round(initialAiScore)" />
                        </div>
                    </div>

                    <p>
                        The AI Scan checks your property for spelling and grammatical errors, inconsistent information, and the originality of your marketing copy. A quality score is then generated. This score is a tool to help improve your listing.  It is only visible to you.
                    </p>
                    <p>
                        Click "Scan Now" to begin the AI Scan.
                    </p>
                </div>


                <div v-if="currentScan === 1 || currentScan === 2" class="results-grid">
                    <div class="explanation-text">
                        <h5 class="card-title mb-0">
                            {{ currentScan === 1 ? 'Language & Grammar Issues' : 'Listing Consistency Issues' }}
                        </h5>
                        <p class="m-0" v-if="currentScan === 1">
                            The AI scan has checked your property details for potential language and grammar issues.  
                            Review any suggestions below and choose to accept or ignore each change.
                        </p>
                        <p class="m-0" v-else>
                            The AI scan has reviewed your property details and amenities for potential inconsistencies—such as conflicting or missing information. 
                            For example, if the description refers to the property as a cabin but the marketing title describes it as a farmhouse, it will be flagged as inconsistent. 
                            Review any suggestions below and choose to accept or ignore each change. 
                        </p>
                    </div>
                    <AiScanResultCard
                        v-for="(item, index) in aiScanResults"
                        :key="index"
                        :item="item"
                        :show-feedback="item.feedback"
                        :show-amenities="currentScan === 2"
                        :render-diff="renderDiff"
                        :update-values="updateValues"
                        :ignore-value="ignoreValue"
                        :set-amenity="setAmenity"
                    />
                    <div
                        v-if="aiScanResults.length === 0"
                        style="display: flex; flex-direction: column; justify-content: center; align-items: center"
                    >
                        <font-awesome-icon
                            :icon="['far', 'circle-check']"
                            style="width: 50px; height: 50px; padding-bottom: 20px; color: #0d47a1"
                        ></font-awesome-icon>
                        <p>Everything looks good; no issues were found.</p>
                        <small>Some issues may go undetected—please review manually if needed.</small>
                    </div>
                </div>

                <div v-if="currentScan === 3" class="results-grid">
                    <h3>{{ wizardData.name }} Score</h3>
                    <div>
                        <AiScoreGraph :score="Math.round(ai_scoring.finalScore || 0)" />
                    </div>
                    
                    <div>
                        <h3>Scoring Breakdown</h3>
                        <ul>
                            <template v-for="(score, key) in ai_scoring" :key="key">
                                <li v-if="key !== 'finalScore'">
                                    <strong>{{ key.charAt(0).toUpperCase() + key.slice(1) }}:</strong> {{ getScoreLabel(score) }}
                                    <ul v-if="getScoreLabel(score) !== 'Excellent'">
                                        <li class="text-muted" style="font-size: 0.95em;">
                                            {{ getScoreImprovementTip(key) }}
                                        </li>
                                    </ul>
                                </li>
                            </template>
                        </ul>
                    </div>

                    <h3 v-if="aiScanResults.length > 0">AI Suggestions</h3>
                    <div class="results-grid">
                        <template v-for="(item, index) in aiScanResults" :key="index">
                            <div v-if="item.feedback" class="result-card">
                                <h5 class="card-title">{{ item.label }}</h5>
                                <div class="card-content">
                                    <p class="text">{{ item.feedback }}</p>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

            </template>
        </div>

        <template #modal-footer="{ ok }" v-if="!isLoading">
            <div class="w-100">
                <b-button
                    :variant="footerPrimaryButton.variant"
                    class="float-right accept-button ml-2"
                    :disabled="false"
                    @click="handleAiPrimaryButton()"
                >
                    {{ footerPrimaryButton.text }}
                </b-button>

                <template v-if="showAcceptAllButton">
                    <b-button
                        v-if="aiScanResults.length > 0"
                        variant="primary"
                        class="float-right"
                        @click="acceptAllChanges()"
                    >
                        Accept All
                    </b-button>
                </template>
            </div>
        </template>
    </modal>
</template>

<script>
import API from '../../../../../api.jsx';
import Modal from '../../../../generic/modal/modal.vue';
import {
    RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT,
    RENTAL_PROPERTY_SETUP_AMENITIES_ENDPOINT,
} from '../../../../../endpoints';
import { formatResponseError } from '../../../../../utils/api';
import AiScoreGraph from '../../../../ai-scan-dashboard/ai-score-graph.vue';
import AiScanResultCard from './ai-scan-result-card.vue';

export default {
    name: 'AiScanModal',
    components: {
        AiScoreGraph,
        Modal,
        AiScanResultCard,
    },
    props: {
        propertyId: {
            type: Number,
            required: true,
        },
        selectedLanguage: {
            type: String,
            required: true,
        },
        values: {
            type: null,
            required: true,
        },
        wizardData: {
            type: null,
            required: true,
        },
        actions: {
            type: Object,
            required: true,
        },
    },

    async mounted() {
        const { data } = await API.get(`${RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT(this.propertyId)}ai-scan-permission/`);
        this.aiScanAllowed = data.ai_scan_allowed;
        if (this.$router.currentRoute._value.query.ai_scan && this.aiScanAllowed) {
            const { ai_scan, ...rest } = this.$router.currentRoute._value.query;
            await this.$router.replace({
                query: rest,
            });
            this.startAiScan();
            this.$bvModal.show('ai-scan-modal');
        }
    },

    data() {
        return {
            aiScanResults: [],
            amenities: null,
            currentScan: null,
            ai_scoring: {
                completeness: 0,
                grammar: 0,
                consistency: 0,
                originality: 0,
                finalScore: 0,
            },
            initialAiScore: 0,
            aiScanAllowed: false,
            isLoading: false,
        };
    },

    computed: {
        aiScanModalTitle() {
            if (this.isLoading) {
                return 'AI Scan - Loading...';
            } else {
                return 'AI Scan';
            }
        },

        footerPrimaryButton() {
            if (this.currentScan === 0) {
                return {
                    text: 'Scan Now',
                    variant: 'primary',
                };
            } else if (this.currentScan === 1 || this.currentScan === 2) {
                return {
                    text: 'Next',
                    variant: 'primary',
                };
            } else if (this.currentScan === 3) {
                return {
                    text: 'Close',
                    variant: 'secondary',
                };
            }
            return {
                text: 'Close',
                variant: 'secondary',
            };
        },

        showAcceptAllButton() {
            return (
                this.currentScan === 1 &&
                this.aiScanResults.length > 0 &&
                !this.aiScanResults.some((item) => item.applied || item.ignored)
            );
        },
    },

    methods: {
        handleAiPrimaryButton() {
            if (this.currentScan === 0) {
                this.startAiScan();
            } else if (this.currentScan === 1) {
                this.aiScanMismatch();
            } else if (this.currentScan === 2) {
                this.aiScanFinalScoring();
            } else {
                this.currentScan = 0;
                this.aiScanResults = [];
                this.$bvModal.hide('ai-scan-modal');
            }
        },

        async startAiScan() {
            if (this.selectedLanguage != 'en') {
                this.errorToast({ text: 'AI scan is only available for English.' });
                this.$bvModal.hide('ai-scan-modal');
                return;
            }
            if (!this.aiScanAllowed) {
                this.errorToast({ text: 'AI scan is not allowed.' });
                this.$bvModal.hide('ai-scan-modal');
                return;
            }
            try {
                this.isLoading = true;
                const { data } = await API.post(`${RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT(this.propertyId)}ai-scan/`);
                let tmpData = data.data.results;
                this.ai_scoring.completeness = data.data.completeness;
                tmpData = tmpData.filter(
                    (item) => item.corrected_value && item.corrected_value !== item.original_value
                );
                tmpData = this.getAiScanFieldLabels(tmpData);
                tmpData = tmpData.map((item) => ({ ...item, opened: true, applied: false, ignored: false }));
                this.aiScanResults = tmpData;
                this.currentScan = 1;
                this.$bvModal.show('ai-scan-modal');
            } catch (e) {
                const { generic } = formatResponseError(e);
                console.log(e);
                this.errorToast({ text: generic || 'Could not scan property' });
            } finally {
                this.isLoading = false;
            }
        },

        async aiScanMismatch() {
            if (this.currentScan === 1) {
                const grammar_score = this.aiScanResults.filter(
                    (item) =>
                        item.corrected_value &&
                        item.corrected_value !== item.original_value &&
                        item.applied === false &&
                        item.ignored === false
                ).length;
                if (grammar_score > 2) {
                    this.ai_scoring.grammar = 1;
                } else if (grammar_score > 0) {
                    this.ai_scoring.grammar = 2;
                } else {
                    this.ai_scoring.grammar = 3;
                }
            }

            if (this.selectedLanguage != 'en') {
                this.errorToast({ text: 'AI scan is only available for English.' });
                return;
            }
            try {
                this.isLoading = true;
                const { data } = await API.post(
                    `${RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT(this.propertyId)}ai-scan-mismatch-check/`
                );
                let tmpData = data.data;
                tmpData = tmpData.filter(
                    (item) =>
                        (item.corrected_value && item.corrected_value !== item.original_value) ||
                        (item.review_amenities && item.review_amenities.length > 0) ||
                        (item.conflicting_fields && item.conflicting_fields.length > 0)
                );
                tmpData = this.getAiScanFieldLabels(tmpData);
                tmpData = tmpData.map((item) => ({ ...item, opened: true, applied: false, ignored: false }));
                this.aiScanResults = tmpData;

                try {
                    const { data } = await API.get(RENTAL_PROPERTY_SETUP_AMENITIES_ENDPOINT(this.propertyId));
                    this.amenities = data;

                    this.aiScanResults.forEach((item) => {
                        if (item.review_amenities && item.review_amenities.length > 0) {
                            item.review_amenities = item.review_amenities
                                .map((amenity) => this.getAmenity(amenity))
                                .filter((amenity) => amenity !== null);
                        }
                    });

                    this.aiScanResults = this.aiScanResults.filter(
                        (item) =>
                            (item.corrected_value && item.corrected_value !== item.original_value) ||
                            (item.review_amenities && item.review_amenities.length > 0) ||
                            (item.conflicting_fields && item.conflicting_fields.length > 0)
                    );
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not fetch amenities',
                    });
                }

                this.currentScan = 2;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not scan property' });
            } finally {
                this.isLoading = false;
            }
        },

        async aiScanFinalScoring() {
            if (this.currentScan === 2) {
                const consistency_score = this.aiScanResults.filter(
                    (item) =>
                        item.corrected_value &&
                        item.corrected_value !== item.original_value &&
                        item.applied === false &&
                        item.ignored === false
                ).length;
                if (consistency_score > 2) {
                    this.ai_scoring.consistency = 1;
                } else if (consistency_score > 0) {
                    this.ai_scoring.consistency = 2;
                } else {
                    this.ai_scoring.consistency = 3;
                }
            }
            if (this.selectedLanguage != 'en') {
                this.errorToast({ text: 'AI scan is only available for English.' });
                return;
            }
            try {
                this.isLoading = true;
                const { data } = await API.post(
                    `${RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT(this.propertyId)}ai-creative-check/`,
                    {
                        grammar_score: this.ai_scoring.grammar,
                        consistency_score: this.ai_scoring.consistency,
                        completeness_score: this.ai_scoring.completeness,
                    }
                );
                let tmpData = this.getAiScanFieldLabels(data.data);
                tmpData = tmpData.filter((item) => !!item.feedback && item.professionalism_and_suitability_score < 3);
                this.aiScanResults = tmpData;
                this.ai_scoring.originality = data.professionalism_and_suitability_score_avg;
                this.ai_scoring.finalScore = data.final_score;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not get property score.' });
            } finally {
                this.isLoading = false;
            }

            this.currentScan = 3;
        },

        getAmenity(amenityName) {
            if (this.amenities && typeof this.amenities === 'object' && amenityName) {
                const searchName = amenityName.toLowerCase();
                for (const category of Object.values(this.amenities)) {
                    if (Array.isArray(category)) {
                        const found = category.find((item) => item.name && item.name.toLowerCase() === searchName);
                        if (found) return found;
                    }
                }
            }
            return null;
        },

        async setAmenity(value, id) {
            try {
                const { data } = await API.post(
                    `${RENTAL_PROPERTY_SETUP_AMENITIES_ENDPOINT(this.propertyId)}set-amenity/`,
                    {
                        id,
                        checked: value,
                    }
                );
                this.amenities = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not set amenity',
                });
            }
        },

        async updateValues(field, value, save = true) {
            try {
                // this.isLoading = true;
                const keys = field.split(/\.|\[|\]/).filter(Boolean);
                let obj = this.values;

                for (let i = 0; i < keys.length - 1; i++) {
                    const key = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
                    if (!obj[key]) {
                        this.$set(obj, key, isNaN(keys[i + 1]) ? {} : []);
                    }
                    obj = obj[key];
                }

                const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1]);
                this.$set(obj, lastKey, value);
                this.actions.commitChangesToStore(this.values);
                if (save) {
                    if (this.values.custom_property_variables) {
                        try {
                            await API.post(
                                `${RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT(this.propertyId)}update-custom-variables/`,
                                this.values.custom_property_variables
                            );
                        } catch (e) {
                            const { generic } = formatResponseError(e);
                            this.errorToast({ text: generic || 'Could not update custom variables' });
                        }
                    }
                    await this.actions.onSave(this.values);
                    this.actions.updateValues(null);
                    await this.actions.fetchDetails();
                    this.aiScanResults = this.aiScanResults.map(
                        (item) =>
                            item.field === field ? { ...item, applied: true, opened: false, ignored: false } : item
                    );
                }
            } catch (e) {
                console.log(e);
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not update value' });
            } finally {
                // this.isLoading = false;
            }
        },

        async ignoreValue(field, value) {
            try {
                this.aiScanResults = this.aiScanResults.map(
                    (item) => (item.field === field ? { ...item, ignored: true, opened: false } : item)
                );
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not ignore value' });
            }
        },

        renderDiff(original, corrected, showRemovals = true) {
            if (!original || !corrected || original === corrected) {
                return original || 'N/A';
            }

            const originalWords = original.split(/\s+/);
            const correctedWords = corrected.split(/\s+/);
            let result = [];

            const lcs = Array(originalWords.length + 1)
                .fill()
                .map(() => Array(correctedWords.length + 1).fill(0));

            for (let i = 1; i <= originalWords.length; i++) {
                for (let j = 1; j <= correctedWords.length; j++) {
                    if (originalWords[i - 1] === correctedWords[j - 1]) {
                        lcs[i][j] = lcs[i - 1][j - 1] + 1;
                    } else {
                        lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1]);
                    }
                }
            }

            let i = originalWords.length;
            let j = correctedWords.length;
            while (i > 0 || j > 0) {
                if (i > 0 && j > 0 && originalWords[i - 1] === correctedWords[j - 1]) {
                    result.unshift(originalWords[i - 1]);
                    i--;
                    j--;
                } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
                    result.unshift(
                        `<span style="background-color: #d1fae5; color: #15803d; padding: 2px 4px; border-radius: 4px; font-weight: 600;">${
                            correctedWords[j - 1]
                        }</span>`
                    );
                    j--;
                } else if (i > 0 && (j === 0 || lcs[i][j - 1] < lcs[i - 1][j])) {
                    if (showRemovals) {
                        result.unshift(
                            `<span style="background-color: #fee2e2; color: #b91c1c; text-decoration: line-through; padding: 2px 4px; border-radius: 4px;">${
                                originalWords[i - 1]
                            }</span>`
                        );
                    }
                    i--;
                }
            }

            return result.join(' ') || 'N/A';
        },

        getAiScanFieldLabels(tmpData) {
            const checkOutTasks = this.values.check_out_tasks || [];
            const customPropertyVariables = this.values.custom_property_variables || [];
            const airbnbExpectation = this.values.airbnb_expectation || [];
            tmpData.forEach((item) => {
                if (item.field.startsWith('check_out_tasks')) {
                    const taskIndex = parseInt(item.field.match(/\d+/)[0]);
                    const index = checkOutTasks.findIndex((task) => task.description === item.original_value);
                    let locationIndex = index;
                    if (index !== -1) {
                        item.field = `check_out_tasks[${index}].description`;
                    } else {
                        locationIndex = taskIndex;
                        item.field = `check_out_tasks[${taskIndex}].description`;
                    }
                    item.label = checkOutTasks[locationIndex]
                        ? 'Check Out Task - ' + checkOutTasks[locationIndex].label
                        : 'Check Out Task';
                } else if (item.field.startsWith('airbnb_expectation')) {
                    const taskIndex = parseInt(item.field.match(/\d+/)[0]);
                    const index = airbnbExpectation.findIndex((task) => task.description === item.original_value);
                    let locationIndex = index;
                    if (index !== -1) {
                        item.field = `airbnb_expectation[${index}].description`;
                    } else {
                        locationIndex = taskIndex;
                        item.field = `airbnb_expectation[${taskIndex}].description`;
                    }
                    item.label = airbnbExpectation[locationIndex]
                        ? 'Airbnb Expectation - ' + airbnbExpectation[locationIndex].label
                        : 'Airbnb Expectation';
                } else if (item.field.startsWith('custom_property_variables')) {
                    const taskIndex = parseInt(item.field.match(/\d+/)[0]);
                    const index = customPropertyVariables.findIndex((task) => task.description === item.original_value);
                    let locationIndex = index;
                    if (index !== -1) {
                        item.field = `custom_property_variables[${index}].value`;
                    } else {
                        locationIndex = taskIndex;
                        item.field = `custom_property_variables[${taskIndex}].value`;
                    }
                    item.label = customPropertyVariables[locationIndex]
                        ? 'Custom Property - ' + customPropertyVariables[locationIndex].title
                        : 'Custom Property';
                } else {
                    const fieldLabelMap = {
                        name: 'Property Name',
                        'marketing_info.title': 'Marketing Title',
                        'marketing_info.teaser': 'Marketing Teaser',
                        description: 'Description',
                        additional_info: 'Additional Info',
                        'check_in_options.instruction': 'Check-in Instructions',
                        house_manual: 'House Manual',
                        directions: 'Directions',
                        parking_info: 'Parking Information',
                    };

                    item.label =
                        fieldLabelMap[item.field] ||
                        item.field
                            .split('.')
                            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                            .join(' ');
                }
            });

            return tmpData;
        },

        async acceptAllChanges() {
            for (const item of this.aiScanResults) {
                await this.updateValues(item.field, item.corrected_value, false);
            }
            await this.actions.onSave(this.values);
            this.actions.updateValues(null);
            await this.actions.fetchDetails();
            this.aiScanResults = [];
            this.handleAiPrimaryButton();
        },

        getScoreLabel(score) {
            if (score > 2.5) {
                return 'Excellent';
            } else if (score > 1.5 && score <= 2.5) {
                return 'Average';
            } else if (score <= 1.5) {
                return 'Poor';
            }
            return 'N/A';
        },
        getScoreImprovementTip(key) {
            const tips = {
                completeness:
                    'Ensure all important fields are thoroughly completed. Include at least three high-quality images in the gallery.',
                grammar: 'Review and correct any spelling or grammatical errors throughout your property.',
                consistency:
                    'Maintain consistent information across all sections, including amenities and property details.',
                originality:
                    'Use clear, engaging, and distinctive language to highlight the unique features of your listing.',
            };
            return tips[key] || 'Review this section for possible improvements.';
        },

        async openAiScanModal() {
            this.currentScan = 0;
            this.isLoading = true;
            this.$bvModal.show('ai-scan-modal');

            try {
                const { data } = await API.get(`${RENTAL_PROPERTY_SETUP_DETAILS_ENDPOINT(this.propertyId)}ai-score/`);
                this.initialAiScore = data.ai_score;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch AI score.' });
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.margin-top-1rem {
    margin-top: 1rem;
}

.scan-results-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 4px;
}

.results-grid {
    display: grid;
    gap: 24px;
}

.result-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 24px;
}

.card-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0px;
}

.card-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.text-block {
    flex: 1;
}

.label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 4px;
}

.text {
    font-size: 1rem;
    color: #374151;
    line-height: 1.6;
}

.explanation-text {
    font-size: 0.9375rem;
    color: #4b5563;
    background: #f9fafb;
    padding: 12px;
    border-radius: 8px;
    line-height: 1.5;
}

.m-top {
    margin-top: 1.5rem;
}

.button-group {
    display: flex;
    gap: 12px;
}
</style>
